import React from 'react';
import { Award, Users, Shield, ArrowRight, Linkedin, Instagram, CheckCircle2, MapPin, Phone, FileCheck, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead, { ADVISOR_NAME, ADVISOR_PHONE, ADVISOR_PHONE_DISPLAY, ADVISOR_EMAIL } from '../components/SEOHead';
import { trackWhatsAppClick, trackPhoneClick } from '../services/analytics';

const About = () => {
  const { t, i18n } = useTranslation();
  const isTe = i18n.language === 'te';

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    'name': isTe ? 'వి. ఎస్. రెడ్డి గురించి | సీనియర్ ఎల్ఐసి సలహాదారు' : 'About V. S. Reddy | Senior Insurance Advisor',
    'description': isTe
      ? 'హైదరాబాద్ మరియు ప్రపంచవ్యాప్త ఎన్నారైలకు 25+ ఏళ్లుగా నమ్మకమైన బీమా సేవలు అందిస్తున్న సీనియర్ ఎల్ఐసి సలహాదారు వి. ఎస్. రెడ్డి ప్రొఫైల్.'
      : 'Profile and credentials of V. S. Reddy, Senior LIC Insurance Advisor with 25+ years of verified expertise in Hyderabad, India.',
    'mainEntity': {
      '@type': 'Person',
      'name': ADVISOR_NAME,
      'jobTitle': 'Senior Life Insurance Advisor & Financial Consultant',
      'worksFor': {
        '@type': 'InsuranceAgency',
        'name': 'VSR Insurance Plus (SVR LIC Services)'
      },
      'knowsAbout': [
        'Life Insurance Corporation of India (LIC)',
        'HDFC ERGO Health Insurance',
        'Retirement & Pension Planning',
        'Child Higher Education Funding',
        'NRI Insurance & FEMA Regulations'
      ],
      'telephone': ADVISOR_PHONE,
      'email': ADVISOR_EMAIL,
      'address': {
        '@type': 'PostalAddress',
        'addressLocality': 'Hyderabad',
        'addressRegion': 'Telangana',
        'addressCountry': 'IN'
      }
    }
  };

  const waMessage = isTe
    ? 'నమస్కారం వి. ఎస్. రెడ్డి గారు, నేను బీమా కన్సల్టేషన్ షెడ్యూల్ చేయాలనుకుంటున్నాను.'
    : 'Hi V. S. Reddy, I would like to schedule an insurance consultation.';

  return (
    <div className="min-h-screen pt-24 pb-20 bg-slate-50 dark:bg-slate-950">
      <SEOHead
        title={isTe ? 'వి. ఎస్. రెడ్డి గురించి | 25+ ఏళ్ల అనుభవజ్ఞులైన ఎల్ఐసి సలహాదారు | VSR Insurance Plus' : 'About V. S. Reddy | 25+ Years Senior LIC Advisor | VSR Insurance Plus'}
        description={isTe ? '2000 నుండి 10,000+ కుటుంబాలకు ఎల్ఐసి లైఫ్ ఇన్సూరెన్స్, చైల్డ్ ప్లాన్లు, పెన్షన్ మరియు హెచ్‌డిఎఫ్‌సి ఎర్గో హెల్త్ పాలసీలలో సలహాలు అందిస్తున్న వి. ఎస్. రెడ్డి.' : 'Meet V. S. Reddy, senior insurance advisor guiding 10,000+ families since 2000 in LIC life insurance, child policies, pensions, and HDFC ERGO health plans in Hyderabad & worldwide.'}
        canonicalPath="/about"
        schemaJson={aboutSchema}
        breadcrumbs={[{ name: isTe ? 'వి. ఎస్. రెడ్డి గురించి' : 'About V. S. Reddy', path: '/about' }]}
      />

      <div className="container mx-auto px-6 max-w-5xl">
        {/* Header content */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-primary text-xs font-bold uppercase tracking-wider mb-4">
            {isTe ? 'సర్టిఫైడ్ బీమా సలహా • 2000 నుండి విశేష సేవలు' : 'Certified Insurance Advisory • Since 2000'}
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 dark:text-white mb-4 tracking-tight">
            {t('trust_behind_name')}
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-3xl mx-auto leading-relaxed">
            {isTe
              ? 'హైదరాబాద్, తెలంగాణ మరియు ప్రపంచవ్యాప్తంగా ఉన్న వేలాది ఎన్నారై కుటుంబాలకు పూర్తి నిజాయితీ, పారదర్శకతతో ఉత్తమ బీమా మార్గదర్శకత్వం.'
              : 'Serving families across Hyderabad, Telangana, and Non-Resident Indians globally with complete integrity and zero sales pressure.'}
          </p>
        </div>

        {/* Narrative / Profile section */}
        <div className="grid lg:grid-cols-12 gap-10 items-center mb-16">
          <div className="lg:col-span-5 bg-gradient-to-br from-slate-900 to-blue-950 rounded-3xl p-8 text-white relative shadow-2xl overflow-hidden border border-slate-800">
            <div className="absolute top-0 right-0 w-48 h-48 bg-primary/20 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" />
            
            <div className="text-center relative z-10 flex flex-col items-center">
              <div className="w-24 h-24 rounded-full bg-white/10 flex items-center justify-center mb-6 border-2 border-yellow-400/50 shadow-inner">
                <Award className="w-12 h-12 text-yellow-400" />
              </div>
              <h2 className="text-3xl font-extrabold mb-1">{isTe ? 'వి. ఎస్. రెడ్డి' : 'V. S. Reddy'}</h2>
              <p className="text-sm text-blue-200 font-semibold tracking-wide uppercase mb-4">
                {isTe ? 'సీనియర్ ఎల్ఐసి సలహాదారు & కన్సల్టెంట్' : 'Senior LIC Advisor & Consultant'}
              </p>
              <div className="flex items-center gap-2 text-xs text-slate-300 bg-white/10 px-3 py-1.5 rounded-full mb-6">
                <MapPin className="w-3.5 h-3.5 text-rose-400" />
                <span>{isTe ? 'హైదరాబాద్, తెలంగాణ • ఎస్వీఆర్ ఎల్ఐసి సర్వీసెస్' : 'Hyderabad, Telangana • SVR LIC Services'}</span>
              </div>

              <div className="grid grid-cols-2 gap-3 w-full border-t border-white/10 pt-6 mb-6 text-left">
                <div>
                  <div className="text-xs text-blue-300 uppercase font-semibold">{isTe ? 'అనుభవం' : 'Experience'}</div>
                  <div className="text-xl font-bold">{isTe ? '25+ సంవత్సరాలు' : '25+ Years'}</div>
                  <div className="text-[11px] text-slate-400">{isTe ? '2000 నుండి' : 'Serving since 2000'}</div>
                </div>
                <div>
                  <div className="text-xs text-blue-300 uppercase font-semibold">{isTe ? 'కుటుంబాలు' : 'Families Guided'}</div>
                  <div className="text-xl font-bold">10,000+</div>
                  <div className="text-[11px] text-slate-400">{isTe ? 'భారత్ & ఎన్నారైలు' : 'Across India & NRIs'}</div>
                </div>
              </div>

              <div className="flex gap-3">
                <a
                  href="https://www.linkedin.com/in/veereddy-srinivas-reddy-23914a5a/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/10 hover:bg-white/20 p-3 rounded-full transition-colors cursor-pointer"
                  aria-label="LinkedIn Profile of V. S. Reddy"
                >
                  <Linkedin className="w-5 h-5 text-sky-400" />
                </a>
                <a
                  href="https://www.instagram.com/vsr_insurance_plus?igsh=MTJ5YWY3dDY4aGhiZw%3D%3D&utm_source=qr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/10 hover:bg-white/20 p-3 rounded-full transition-colors cursor-pointer"
                  aria-label="Instagram Profile of VSR Insurance Plus"
                >
                  <Instagram className="w-5 h-5 text-rose-400" />
                </a>
                <a
                  href={`tel:${ADVISOR_PHONE}`}
                  onClick={() => trackPhoneClick('about_card_phone')}
                  className="bg-primary hover:bg-blue-600 p-3 rounded-full transition-colors cursor-pointer"
                  aria-label="Call V. S. Reddy directly"
                >
                  <Phone className="w-5 h-5 text-white" />
                </a>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-6">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white leading-tight">
              {t('quarter_century')}
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              {t('guide_light_desc')}
            </p>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              {t('whether_ambitious')}
            </p>

            <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800 space-y-3">
              <h3 className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-primary" /> {isTe ? 'ప్రత్యేక నైపుణ్య విభాగాలు' : 'Key Areas of Specialization'}
              </h3>
              <ul className="grid sm:grid-cols-2 gap-2 text-sm text-slate-600 dark:text-slate-400">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span>{isTe ? 'ఎల్ఐసి గ్యారెంటీడ్ పెన్షన్ (జీవన్ ఉత్సవ్ / శాంతి)' : 'LIC Guaranteed Pension (Jeevan Utsav / Shanti)'}</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span>{isTe ? 'పిల్లల ఉన్నత విద్య (అమృతబాల్ / లక్ష్య)' : 'Child Higher Education (Amrit Baal / Lakshya)'}</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span>{isTe ? 'హెచ్‌డిఎఫ్‌సి ఎర్గో 4X హెల్త్ ఫ్లోటర్ ప్లాన్లు' : 'HDFC ERGO 4X Health Floater Plans'}</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span>{isTe ? 'వయోవృద్ధ తల్లిదండ్రుల మెడిక్లెయిమ్' : 'Senior Citizen Parents Mediclaim'}</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span>{isTe ? 'ఎన్నారై మెయిల్ ఆర్డర్ బిజినెస్ (MOB) & ఫెమా' : 'NRI Mail Order Business (MOB) & FEMA'}</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span>{isTe ? 'హైదరాబాద్‌లో ఇంటివద్దకే పాలసీ సర్వీసింగ్' : 'Doorstep Documentation in Hyderabad'}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Our 4-Step Consultation Process */}
        <div className="mb-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-2">
              {isTe ? 'మా 4-దశల సలహా ప్రక్రియ' : 'Our 4-Step Advisory Process'}
            </h2>
            <p className="text-slate-500 text-sm max-w-xl mx-auto">
              {isTe
                ? 'ఎలాంటి కన్ఫ్యూజన్ లేదా సేల్స్ ఒత్తిడి లేకుండా మీ కుటుంబానికి సరైన బీమా రక్షణను ఎంచుకునే విధానం.'
                : 'How we work together to protect your family without confusing jargon or high-pressure tactics.'}
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm relative">
              <span className="text-3xl font-extrabold text-primary/20 mb-3 block">01</span>
              <h3 className="font-bold text-slate-900 dark:text-white mb-2">{isTe ? 'అవసరాల విశ్లేషణ' : 'Needs Analysis'}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                {isTe
                  ? 'మీ కుటుంబ దశ, నెలవారీ ఖర్చులు, ప్రస్తుత బాధ్యతలు మరియు భవిష్యత్ లక్ష్యాలను విశ్లేషిస్తాము.'
                  : 'We review your family stage, monthly expenses, existing liabilities, and milestones.'}
              </p>
            </div>

            <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm relative">
              <span className="text-3xl font-extrabold text-sky-500/20 mb-3 block">02</span>
              <h3 className="font-bold text-slate-900 dark:text-white mb-2">{isTe ? 'పాలసీ ఎంపిక' : 'Policy Selection'}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                {isTe
                  ? 'ప్రభుత్వ హామీ గల ఎల్ఐసి ప్లాన్లు మరియు హెచ్‌డిఎఫ్‌సి ఎర్గో హెల్త్ పాలసీలను మీ బడ్జెట్‌కు తగినట్లు సరిపోల్చుతాము.'
                  : 'Compare sovereign-backed LIC options and HDFC ERGO health plans matching your exact budget.'}
              </p>
            </div>

            <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm relative">
              <span className="text-3xl font-extrabold text-emerald-500/20 mb-3 block">03</span>
              <h3 className="font-bold text-slate-900 dark:text-white mb-2">{isTe ? 'సులభమైన కేవైసీ' : 'Hassle-Free KYC'}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                {isTe
                  ? 'హైదరాబాద్‌లో ఇంటివద్దకే డాక్యుమెంట్ సేకరణ లేదా ఎన్నారైలకు 100% డిజిటల్ మెయిల్ ఆర్డర్ ప్రాసెసింగ్.'
                  : 'Doorstep pickup in Hyderabad or 100% digital Mail Order processing for NRIs globally.'}
              </p>
            </div>

            <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm relative">
              <span className="text-3xl font-extrabold text-rose-500/20 mb-3 block">04</span>
              <h3 className="font-bold text-slate-900 dark:text-white mb-2">{isTe ? 'జీవితాంతం సేవలు' : 'Lifetime Support'}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                {isTe
                  ? 'చిరునామా మార్పులు, నెఫ్ట్ వివరాలు, లోన్ సదుపాయం మరియు క్లెయిమ్ సమయంలో స్వయంగా అండగా నిలుస్తాము.'
                  : 'Address changes, NEFT updates, loan processing, and personal claim settlement support when it matters.'}
              </p>
            </div>
          </div>
        </div>

        {/* Values Section */}
        <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 md:p-12 shadow-sm border border-slate-200 dark:border-slate-800 mb-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">{t('core_pillars')}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-3">
              <Shield className="w-10 h-10 text-primary mx-auto" />
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">{t('absolute_transparency')}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{t('transparency_desc')}</p>
            </div>
            <div className="text-center space-y-3">
              <Users className="w-10 h-10 text-sky-500 mx-auto" />
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">{t('personal_attention')}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{t('attention_desc')}</p>
            </div>
            <div className="text-center space-y-3">
              <Award className="w-10 h-10 text-emerald-500 mx-auto" />
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">{t('lifelong_service')}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{t('service_desc')}</p>
            </div>
          </div>
        </div>

        {/* Regulatory Disclosure & Disclaimers */}
        <div className="bg-slate-100 dark:bg-slate-900/60 rounded-2xl p-6 border border-slate-200 dark:border-slate-800 text-xs text-slate-500 dark:text-slate-400 space-y-2 mb-12">
          <div className="font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
            <HelpCircle className="w-4 h-4 text-primary" /> {isTe ? 'చట్టబద్ధమైన సలహా బహిర్గతం:' : 'Statutory Advisory Disclosure:'}
          </div>
          <p>
            {isTe
              ? 'విఎస్ఆర్ ఇన్సూరెన్స్ ప్లస్ (ఎస్వీఆర్ ఎల్ఐసి సర్వీసెస్ కింద నిర్వహింపబడుతుంది) లైసెన్స్ పొందిన సీనియర్ సలహాదారు వి. ఎస్. రెడ్డి ఆధ్వర్యంలో స్వతంత్ర బీమా కన్సల్టెన్సీగా పనిచేస్తుంది. మేము లైఫ్ ఇన్సూరెన్స్ కార్పొరేషన్ ఆఫ్ ఇండియా (ఎల్ఐసి) మరియు హెచ్‌డిఎఫ్‌సి ఎర్గో జనరల్ ఇన్సూరెన్స్ కంపెనీ తరపున ప్రాతినిధ్యం వహిస్తాము.'
              : 'VSR Insurance Plus (operating under SVR LIC Services) is an independent insurance consultancy and authorized agency led by licensed advisor V. S. Reddy. We represent Life Insurance Corporation of India (LIC) and HDFC ERGO General Insurance Company Ltd.'}
          </p>
          <p>
            {isTe
              ? 'విఎస్ఆర్ ఇన్సూరెన్స్ ప్లస్ అనేది ఎల్ఐసి (licindia.in) లేదా హెచ్‌డిఎఫ్‌సి ఎర్గోల అధికారిక కార్పొరేట్ వెబ్‌సైట్ కాదు. బీమా అనేది అభ్యర్థన విషయం. పాలసీ నిబంధనలు, బోనస్‌లు మరియు సెక్షన్ 80సి, 10(10డి) పన్ను ప్రయోజనాలు ప్రభుత్వ చట్టాలకు మరియు సంబంధిత బీమా సంస్థ అండర్‌రైటింగ్ నిబంధనలకు లోబడి ఉంటాయి.'
              : 'VSR Insurance Plus is not the official corporate website of LIC of India (licindia.in) or HDFC ERGO. Insurance is the subject matter of solicitation. Policy terms, bonuses, and tax exemptions under Section 80C and Section 10(10D) are subject to prevailing laws and underwriting guidelines of the respective insurer.'}
          </p>
        </div>

        {/* Call to action */}
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
            {isTe ? 'నిజాయితీతో కూడిన బీమా సలహా కోసం సిద్ధంగా ఉన్నారా?' : 'Ready for Honest, Conflict-Free Insurance Advice?'}
          </h2>
          <div className="flex flex-col sm:flex-row justify-center gap-4 pt-2">
            <a
              href={`https://wa.me/919849664800?text=${encodeURIComponent(waMessage)}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick('about_page_cta')}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary hover:bg-blue-600 text-white rounded-xl font-bold shadow-lg transition-all hover:-translate-y-1"
            >
              {isTe ? 'వాట్సాప్‌లో సంప్రదించండి' : 'WhatsApp V. S. Reddy'} <ArrowRight className="w-4 h-4" />
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl font-bold hover:shadow-xl transition-all hover:-translate-y-1"
            >
              {t('schedule_consultation')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
